'use client';

import React from 'react';
import { motion } from 'framer-motion';
import { cn } from '@/lib/utils';

interface Tweet {
    id: string;
    name: string;
    handle: string;
    content: string;
    verified?: boolean;
}

const tweets: Tweet[] = [
    {
        id: '1',
        name: 'Gabriel',
        handle: '@_GABRIEL_C_R_',
        content: "The breakdown of Linear’s issue-tracking growth loop in Axist is unmatched. I’ve been trying to reverse engineer this for months. They just handed it to me.",
        verified: true,
    },
    {
        id: '2',
        name: 'Amol Sarva',
        handle: '@amol',
        content: "Founders often ask me how to scale early GTM. I used to send book recommendations. Now I just tell them to look at the Axist playbooks. It’s the cheat sheet.",
        verified: true,
    },
    {
        id: '3',
        name: 'Sarah McInerney',
        handle: '@SarahAMcInerny',
        content: "Rarely impressed by marketing software, but Axist’s daily task generator is genuinely helpful. It cuts through the decision fatigue of 'what should I post today?'",
        verified: true,
    },
    {
        id: '4',
        name: 'Moses John',
        handle: '@johninmarketing',
        content: "Just deployed the 'Cold Outreach' playbook from Axist for my agency. 24% reply rate on the first campaign. This is not normal.",
        verified: false,
    },
    {
        id: '5',
        name: 'Cocosweets',
        handle: '@cocosweets__',
        content: "Okay, the 'Community Building' playbook is practically a masterclass. I’ve learned more in 20 mins here than in that $500 course I took last year. 💀",
        verified: false,
    },
];

const TweetCard = ({ tweet }: { tweet: Tweet }) => {
    return (
        <div className="flex-shrink-0 w-[350px] mx-4 p-6 bg-white dark:bg-neutral-900 rounded-2xl border border-neutral-200 dark:border-neutral-800 shadow-sm">
            <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                    <div>
                        <div className="flex items-center gap-1">
                            <span className="font-semibold text-neutral-900 dark:text-white text-sm">
                                {tweet.name}
                            </span>
                            {tweet.verified && (
                                <svg className="w-4 h-4 text-blue-500" viewBox="0 0 24 24" fill="currentColor">
                                    <path d="M22.5 12.5c0-1.58-.875-2.95-2.148-3.6.154-.435.238-.905.238-1.4 0-2.21-1.71-3.998-3.818-3.998-.47 0-.92.084-1.336.25C14.818 2.415 13.51 1.5 12 1.5s-2.816.917-3.437 2.25c-.415-.165-.866-.25-1.336-.25-2.11 0-3.818 1.79-3.818 4 0 .495.083.965.238 1.4-1.272.65-2.147 2.018-2.147 3.6 0 1.495.782 2.798 1.942 3.486-.02.17-.032.34-.032.514 0 2.21 1.708 4 3.818 4 .47 0 .92-.086 1.335-.25.62 1.334 1.926 2.25 3.437 2.25 1.512 0 2.818-.916 3.437-2.25.415.163.865.248 1.336.248 2.11 0 3.818-1.79 3.818-4 0-.174-.012-.344-.033-.513 1.158-.687 1.943-1.99 1.943-3.484zm-6.616-3.334l-4.334 6.5c-.145.217-.382.334-.625.334-.143 0-.288-.04-.416-.126l-.115-.094-2.415-2.415c-.293-.293-.293-.768 0-1.06s.768-.294 1.06 0l1.77 1.767 3.825-5.74c.23-.345.696-.436 1.04-.207.346.23.44.696.21 1.04z" />
                                </svg>
                            )}
                        </div>
                        <div className="text-neutral-500 text-xs">{tweet.handle}</div>
                    </div>
                </div>
                <div className="text-neutral-400">
                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                    </svg>
                </div>
            </div>
            <p className="text-neutral-700 dark:text-neutral-300 text-sm leading-relaxed">
                {tweet.content}
            </p>
        </div>
    );
};

export const TweetCarousel = () => {
    return (
        <div className="w-full py-20 bg-[#FAFAFA] dark:bg-black overflow-hidden">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 mb-10 text-center">
                <h2 className="text-3xl font-bold tracking-tight text-neutral-900 dark:text-white sm:text-4xl">
                    Loved by modern marketers
                </h2>
                <p className="mt-4 text-lg text-neutral-600 dark:text-neutral-400">
                    Join thousands of founders and marketers using Axist to grow.
                </p>
            </div>

            <div className="relative flex">
                {/* Gradients for smooth fade effect at edges */}
                <div className="absolute left-0 top-0 bottom-0 w-20 bg-gradient-to-r from-[#FAFAFA] to-transparent z-10 dark:from-black" />
                <div className="absolute right-0 top-0 bottom-0 w-20 bg-gradient-to-l from-[#FAFAFA] to-transparent z-10 dark:from-black" />

                <motion.div
                    className="flex"
                    animate={{ x: [0, -100 * tweets.length * 5] }} // Adjust speed by changing value
                    transition={{
                        x: {
                            repeat: Infinity,
                            repeatType: "loop",
                            duration: 40, // Slower duration for readability
                            ease: "linear",
                        },
                    }}
                >
                    {/* Double the tweets to create seamless loop */}
                    {[...tweets, ...tweets, ...tweets, ...tweets].map((tweet, index) => (
                        <TweetCard key={`${tweet.id}-${index}`} tweet={tweet} />
                    ))}
                </motion.div>
            </div>
        </div>
    );
};
